import { FunctionComponent, useState, useEffect } from "react";
import { connect } from "react-redux";
import { themeColors } from "../../theme";
import ColorPicker from "react-pick-color";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import { fetchpropertyType } from "../../../Services/contacts";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import InputTextValue from "../../TextInput/InputTextValue";
import DropDownText from "../../../Components/DropDownText/DropDownText";

/* ------------------------------------------------------------------
   TYPES
-------------------------------------------------------------------*/
type props = {
  open: boolean;
  close: () => void;
  heading: string;
  title: string;
  onSave: (v: any, c: any) => void;
  theme: any;
  user: any;
  conatctHeader: any;
  leadSourceColour?: any;
};

/* ------------------------------------------------------------------
   COMPONENT
-------------------------------------------------------------------*/
const EditResources: FunctionComponent<props> = ({
  open,
  close,
  heading,
  title,
  onSave,
  theme,
  user,
  conatctHeader,
  leadSourceColour
}) => {

  /* ---------------------------------------------
   THEME + RESPONSIVENESS
----------------------------------------------*/
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /* ---------------------------------------------
   STATE
----------------------------------------------*/
  const [propertyTypeList, setPropertyTypeList] = useState<{ label: string; value: string }[]>([]);
  const [propertyTypeSelected, setPropertyTypeSelected] = useState<any>({ label: "Select", value: "Select" });
  const [value, setValue] = useState(title);
  const [color, setColor] = useState(leadSourceColour);
  const [propertyTypeValue, setpropertyTypeValue] = useState<{ label: string; value: string }>({ label: "Select", value: "Select" });

  /* ---------------------------------------------
   HANDLE PROPERTY TYPE CHANGE SYNC
----------------------------------------------*/
  useEffect(() => {
    setpropertyTypeValue({
      label: propertyTypeSelected.label,
      value: propertyTypeSelected.value
    });
  }, [propertyTypeSelected]);

  /* ---------------------------------------------
   FETCH PROPERTY TYPES FOR DROPDOWN
----------------------------------------------*/
  useEffect(() => {
    const unsubProperty = fetchpropertyType(
      user.organization_id,
      (data) => {
        const formatted = Array.isArray(data)
          ? data.map((item: any) =>
            typeof item === "string"
              ? { label: item, value: item }
              : item
          )
          : [];
        setPropertyTypeList(formatted);
      }
    );
    return () => {
      unsubProperty();
    };
  }, []);

  /* ---------------------------------------------
   MODAL STYLING
----------------------------------------------*/
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /* ------------------------------------------------------------------
   RENDER UI
-------------------------------------------------------------------*/
  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <form style={{ width: "100%" }}>

          {/* -------------------- HEADER -------------------- */}
          <Box sx={{ mb: 2 }}>
            <h3
              style={{
                color: theme ? "#272944" : "#fff",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-large)",
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {heading}
            </h3>
          </Box>

          {/* -------------------- INPUT FIELDS -------------------- */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12}>
              <InputTextValue
                title={heading}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </Grid>

            {/* Color Picker for Lead Source */}
            <Grid item xs={12}>
              {heading === "Lead Source" && (
                <>
                  <Box sx={{ pl: isMobile ? 3 : 1.5 }}>
                    <ColorPicker color={color} onChange={(color) => setColor(color.hex)} />
                  </Box>
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              {heading === "Edit Property Sub Type" && (
                <DropDownText
                  title={`${conatctHeader.find((obj: any) => obj.label === "property_type").value}`}
                  options={propertyTypeList}
                  value={propertyTypeSelected?.value === "Select" ? "" : propertyTypeSelected?.value}
                  onChange={(e: any) => setPropertyTypeSelected({ value: e.target.value, label: e.target.value })}
                  labelKey="label"
                  valueKey="value"
                />
              )}
            </Grid>
          </Grid>

          {/* -------------------- FOOTER BUTTONS -------------------- */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                width: "100%",
              }}
            >
              <Box style={{ marginRight: 8 }}>
                <StyledCancelButton onClick={close} isMobile={isMobile}>
                  Cancel
                </StyledCancelButton>
              </Box>
              <Box>
                {heading !== "Edit Property Sub Type" ? (
                  <StyledSubmitButton
                    onClick={() => onSave(value, color)}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    Save
                  </StyledSubmitButton>
                ) : (
                  <StyledSubmitButton
                    onClick={() => onSave(value, propertyTypeValue?.value)}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    Save
                  </StyledSubmitButton>
                )}
              </Box>
            </Box>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

/* ------------------------------------------------------------------
   REDUX
-------------------------------------------------------------------*/
const mapStateToProps = (state: any) => {
  return {
    contacts: state.contacts,
    theme: state.theme.isLightMode,
    user: state.user.data,
    conatctHeader: state.contactHeaderList.data,
  };
};

export default connect(mapStateToProps)(EditResources);
